<template>
  <div class="help-center-page">
    <HeadNav :selectedLanguage="selectedLanguage" :page="'help-center'" />
    <Breadcrumbs :items="[{ name: 'Help Center' }]" />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1>How can we help you today?</h1>
        <div class="search-container">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input v-model="searchQuery" type="text" class="search-input" placeholder="Search guides, fixes, privacy tips...">
        </div>
      </div>
    </section>

    <!-- Category Grid -->
    <section class="category-section">
      <div class="grid-container">
        <a href="javascript:void(0)" class="category-card" @click="scrollToSection('privacy')">
          <div class="icon-circle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h3>Privacy</h3>
          <p>Ghost Mode & Blue Ticks</p>
        </a>
        <a href="javascript:void(0)" class="category-card" @click="scrollToSection('media')">
          <div class="icon-circle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <h3>Media</h3>
          <p>Large File Sharing & HD Photos</p>
        </a>
        <a href="javascript:void(0)" class="category-card" @click="scrollToSection('safety')">
          <div class="icon-circle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <h3>Safety</h3>
          <p>Anti-Ban Tips & Fixes</p>
        </a>
        <a href="javascript:void(0)" class="category-card" @click="scrollToSection('basics')">
          <div class="icon-circle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <h3>Basics</h3>
          <p>Backup & Update Guides</p>
        </a>
      </div>
    </section>

    <!-- FAQ Accordion Section -->
    <section id="faq-section" class="faq-section">
      <div class="faq-container">
        <h2>Frequently Asked Questions</h2>
        <div class="accordion">
          <div v-for="(faq, index) in faqs" :key="index" class="accordion-item" :class="{ active: activeFaq === index }">
            <button class="accordion-header" :aria-expanded="activeFaq === index" @click="toggleFaq(index)">
              {{ faq.question }}
              <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div class="accordion-content" :style="{ maxHeight: activeFaq === index ? faqHeight[index] + 'px' : '0px' }">
              <div :ref="'faqContent' + index" class="content-inner" v-html="faq.answer" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Additional Content Sections -->
    <section class="additional-content">
      <div class="content-grid">
        <div class="content-block">
          <h3>Popular Fixes</h3>
          <ul>
            <li><a href="#">Fixing delayed messages</a></li>
            <li><a href="#">Resolve "Theme not supported" error</a></li>
            <li><a href="#">Fixing the "App Not Installed" error</a></li>
          </ul>
        </div>
        <div class="content-block">
          <h3>Avoid Getting Banned</h3>
          <p>Learn the safe practices to keep your account secure while using modded features.</p>
          <a href="#faq-section" class="cta-link">Read Safety Tips &rarr;</a>
        </div>
        <div class="content-block">
          <h3>Restore Backups Safely</h3>
          <p>Step-by-step guide to moving your chats from official WhatsApp to GB WhatsApp.</p>
          <a href="/themes/" class="cta-link">Explore Themes &rarr;</a>
        </div>
      </div>
    </section>

    <!-- Comprehensive Guide Section -->
    <section class="comprehensive-guide">
      <div class="guide-container">
        <h2>Complete Guide to Troubleshooting GB WhatsApp Issues</h2>
        <p>Welcome to our comprehensive troubleshooting hub. Whether you're a new user trying to figure out how to install the app, or a veteran user dealing with a sudden crash, this guide covers everything you need to know to keep your GB WhatsApp running smoothly.</p>

        <h2>How to Fix GB WhatsApp "App Not Installed" Error</h2>
        <p>One of the most frustrating issues users face is the "App Not Installed" error during the initial setup. This usually happens for a few specific reasons. First, Android's built-in security system, Google Play Protect, often flags modified APKs. To bypass this, open the Google Play Store, tap your profile icon, select Play Protect, hit the gear icon, and temporarily disable "Scan apps with Play Protect".</p>
        <p>Second, check your storage space. GB WhatsApp requires at least 2GB of free space to install properly and manage local backups. Finally, ensure you don't have a conflicting package installed. If you are trying to install the <code>com.whatsapp</code> variant of GB WhatsApp, you must completely uninstall the official WhatsApp first (don't forget to backup your chats!).</p>

        <h2>Mastering GB WhatsApp Privacy Features & Ghost Mode</h2>
        <p>The main reason millions of users switch to GB WhatsApp is the unparalleled control over privacy. The crown jewel of these features is "Ghost Mode". When activated, Ghost Mode freezes your "Last Seen" timestamp, hides the blue ticks (read receipts) until you actually reply, and conceals the second tick (delivered receipt) from the sender. It even hides your "Typing..." and "Recording audio..." indicators.</p>
        <p>To enable these features individually without turning on full Ghost Mode, navigate to <strong>GB Settings > Privacy and Security</strong>. Here, you can also enable "Anti-Delete Messages" and "Anti-Delete Status", which allow you to see texts and stories even after the sender has deleted them. Remember, while these features are powerful, using them responsibly ensures a better experience for everyone.</p>

        <h2>How to Safely Update GB WhatsApp Without Losing Chats</h2>
        <p>Updating your app shouldn't mean losing your precious conversations. The golden rule of updating GB WhatsApp is: <strong>Never uninstall the old version.</strong> When a new update is released, verify the APK file from a source you trust before installing it. Tap the downloaded file and select "Install". Your Android device will recognize that it's an update to an existing app and will safely overwrite the core files while leaving your database and media folders completely untouched.</p>
        <p>As a precaution, we always recommend going to <strong>Settings > Chats > Chat Backup</strong> to create a fresh local backup right before you install the new APK. This guarantees that even if the installation fails, your data is safe.</p>

        <h2>GB WhatsApp Anti-Ban Strategies: Keeping Your Account Safe</h2>
        <p>Account bans are a valid concern when using modified apps. WhatsApp's automated systems constantly look for unnatural behavior. To avoid getting a temporary 1-hour or 24-hour ban, you must use the app smartly. Always ensure you are running the latest version of GB WhatsApp, as developers frequently update the anti-ban scripts to bypass new security measures.</p>
        <p>Furthermore, avoid "spammy" behavior. Do not use the mass-message sender to blast messages to unsaved numbers, and avoid adding hundreds of people to groups simultaneously. If you do receive a temporary ban, do not panic. Wait for the timer to expire, backup your chats, temporarily switch back to the official WhatsApp for a few days to clear your account's flagged status, and then reinstall the latest GB WhatsApp version.</p>

        <h2>Managing Media: Sending Large Files and HD Photos</h2>
        <p>Official WhatsApp heavily compresses images and limits video file sizes, which ruins the quality of your shared memories. GB WhatsApp removes these restrictions. By going into <strong>GB Settings > Universal > Settings</strong>, you can adjust the image resolution limit, allowing you to send photos in their original, uncompressed HD quality (up to 3MB per image).</p>
        <p>You can also increase the video sending limit from the standard 16MB up to 50MB or more, depending on the specific version you are using. This makes GB WhatsApp an excellent tool for sharing high-quality media with friends and family without needing to use third-party cloud storage links.</p>

        <h2>Customizing GB WhatsApp: Themes and Fonts</h2>
        <p>Beyond privacy, customization is where GB WhatsApp truly shines. The built-in Theme Store offers thousands of community-created layouts. To access it, go to <strong>GB Settings > Themes > Download Themes</strong>. You can instantly apply iOS-style layouts, dark mode interfaces, or colorful anime themes with a single tap.</p>
        <p>If you prefer a more personalized touch, you can manually adjust almost every UI element. From the chat bubble styles to the font type and the app icon color on your home screen, the possibilities are endless. Just remember that applying very heavy XML themes can sometimes slow down older devices, so stick to optimized themes if performance is a priority.</p>
      </div>
    </section>

    <!-- <Footer :selectedLanguage="selectedLanguage" /> -->
  </div>
</template>

<script>
import HeadNav from "@/components/HeadNav.vue";
import Footer from "@/components/Footer.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";

export default {
  name: "HelpCenterPage",
  components: {
    HeadNav,
    Footer,
    Breadcrumbs
  },
  data() {
    return {
      selectedLanguage: "en",
      searchQuery: "",
      activeFaq: 0, // First FAQ open by default
      faqHeight: {},
      faqs: [
        {
          question: "How to update to v18.80 without losing chats?",
          answer: `<p>Updating GB WhatsApp is safe if you follow these steps to protect your data:</p>
                  <ol>
                    <li>Open your current GB WhatsApp, go to <strong>Settings > Chats > Chat Backup</strong> and create a local backup.</li>
                    <li>Do <strong>NOT</strong> uninstall your current version.</li>
                    <li>Verify the latest v18.80 APK file from a source you trust.</li>
                    <li>Install the new APK directly over the old one. Your phone will prompt "Do you want to install an update to this existing application?". Tap <strong>Install</strong>.</li>
                    <li>Open the app, and all your chats and settings will remain intact.</li>
                  </ol>`
        },
        {
          question: "Fixing the 'App Not Installed' error",
          answer: `<p>This is a common Android error. Try these troubleshooting tips:</p>
                  <ul>
                    <li><strong>Disable Play Protect:</strong> Open Google Play Store > Profile Icon > Play Protect > Settings (gear icon) > Turn off "Scan apps with Play Protect".</li>
                    <li><strong>Clear Storage:</strong> Ensure you have at least 2GB of free space on your device.</li>
                    <li><strong>Uninstall Official WhatsApp:</strong> If you are trying to install the <code>com.whatsapp</code> package, you must uninstall the official WhatsApp first (remember to backup!).</li>
                    <li><strong>Clear Package Installer Cache:</strong> Go to Phone Settings > Apps > Package Installer > Clear Cache and Data.</li>
                  </ul>`
        },
        {
          question: "How to use Anti-Delete to see unsent messages",
          answer: `<p>The Anti-Delete feature allows you to read messages and view statuses even after the sender has deleted them.</p>
                  <ol>
                    <li>Tap the three dots (menu) in the top right corner.</li>
                    <li>Select <strong>GB Settings</strong>.</li>
                    <li>Go to <strong>Privacy and Security</strong>.</li>
                    <li>Scroll down to the Chats section and toggle on <strong>Anti-Delete Messages</strong>.</li>
                    <li><em>Note: Deleted messages will show a small "🚫" icon next to the timestamp, indicating the sender tried to delete it.</em></li>
                  </ol>`
        },
        {
          question: "Enable/Disable Ghost Mode for maximum privacy",
          answer: `<p>Ghost Mode is the ultimate privacy tool. When enabled, your actions are completely hidden.</p>
                  <ol>
                    <li>Open GB WhatsApp and tap the <strong>Ghost Icon</strong> 👻 at the top of the main screen.</li>
                    <li>Confirm the activation.</li>
                  </ol>
                  <p><strong>What Ghost Mode does:</strong></p>
                  <ul>
                    <li>Freezes your "Last Seen" time.</li>
                    <li>Hides the blue ticks (read receipts) until you reply.</li>
                    <li>Hides the second tick (delivered receipt).</li>
                    <li>Hides your "Typing..." and "Recording..." status.</li>
                    <li>Allows you to view others' statuses secretly.</li>
                  </ul>`
        }
      ]
    };
  },
  mounted() {
    this.calculateHeights();
    window.addEventListener('resize', this.calculateHeights);
    this.injectSchema();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateHeights);
  },
  methods: {
    scrollToSection(category) {
      // Map categories to FAQ indices
      const categoryMap = {
        'privacy': 3, // Ghost Mode
        'media': 2,   // Anti-Delete (closest to media/sharing)
        'safety': 1,  // App Not Installed / Fixes
        'basics': 0   // Update Guides
      };
      
      const targetIndex = categoryMap[category];
      if (targetIndex !== undefined) {
        // Open the relevant FAQ
        this.activeFaq = targetIndex;
        
        // Scroll to the FAQ section smoothly
        const faqSection = document.getElementById('faq-section');
        if (faqSection) {
          faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    },
    calculateHeights() {
      this.$nextTick(() => {
        this.faqs.forEach((_, index) => {
          const el = this.$refs['faqContent' + index][0];
          this.$set(this.faqHeight, index, el.scrollHeight);
        });
      });
    },
    injectSchema() {
      const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": this.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer.replace(/<[^>]*>?/gm, '') // Strip HTML for schema text
          }
        }))
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }
};
</script>

<style scoped>
.help-center-page {
  --deep-emerald: #0B141A;
  --wa-green: #25D366;
  --accent-green: #1ebe5d;
  --text-primary: #121212;
  --text-secondary: #4A5568;
  --bg-primary: #FFFFFF;
  --bg-secondary: #F5F7FA;
  --soft-border: rgba(0, 0, 0, 0.08);

  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  min-height: 100vh;
}

/* Hero Section */
.hero-section {
  padding: 30px 20px 60px;
  text-align: center;
  background: linear-gradient(180deg, rgba(37, 211, 102, 0.05) 0%, rgba(255, 255, 255, 1) 100%);
}

.hero-content h1 {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 40px;
  color: var(--text-primary);
}

.search-container {
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  background: #FFFFFF;
  border: 1px solid var(--soft-border);
  border-radius: 30px;
  padding: 5px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.search-container:focus-within {
  border-color: var(--wa-green);
  box-shadow: 0 0 20px rgba(37, 211, 102, 0.15);
}

.search-icon {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: var(--text-secondary);
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  padding: 15px 20px 15px 50px;
  color: var(--text-primary);
  font-size: 1.1rem;
  outline: none;
}

.search-input::placeholder {
  color: #A0AEC0;
}

/* Category Grid */
.category-section {
  padding: 40px 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
}

.category-card {
  background: #FFFFFF;
  border: 1px solid var(--soft-border);
  border-radius: 16px;
  padding: 30px 20px;
  text-align: center;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.category-card:hover {
  transform: translateY(-8px);
  background: #FFFFFF;
  border-color: rgba(37, 211, 102, 0.3);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
}

.icon-circle {
  width: 64px;
  height: 64px;
  background: var(--bg-secondary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.category-card:hover .icon-circle {
  background: var(--wa-green);
  /* color: #FFFFFF; */
}

.icon-circle svg {
  width: 32px;
  height: 32px;
  /* stroke: var(--text-primary); */
}

.category-card:hover .icon-circle svg {
  /* stroke: #FFFFFF; */
}

.category-card h3 {
  font-size: 1.2rem;
  margin-bottom: 8px;
}

.category-card p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
}

/* FAQ Accordion */
.faq-section {
  padding: 60px 20px;
  max-width: 800px;
  margin: 0 auto;
}

.faq-section h2 {
  text-align: center;
  font-size: 2rem;
  margin-bottom: 40px;
  color: var(--text-primary);
}

.accordion-item {
  background: #FFFFFF;
  border: 1px solid var(--soft-border);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.accordion-item.active {
  border-color: rgba(37, 211, 102, 0.3);
  background: var(--bg-secondary);
}

.accordion-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.chevron {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
  color: var(--text-primary);
}

.accordion-item.active .chevron {
  transform: rotate(180deg);
}

.accordion-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-inner {
  padding: 0 20px 20px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.content-inner :deep(ol), .content-inner :deep(ul) {
  padding-left: 20px;
  margin-top: 10px;
}

.content-inner :deep(li) {
  margin-bottom: 8px;
}

.content-inner :deep(strong) {
  color: var(--text-primary);
}

.content-inner :deep(a) {
  color: var(--wa-green);
  text-decoration: none;
}

.content-inner :deep(a:hover) {
  text-decoration: underline;
}

.content-inner :deep(code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  color: var(--text-primary);
}

/* Additional Content */
.additional-content {
  padding: 40px 20px 80px;
  max-width: 1000px;
  margin: 0 auto;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
}

.content-block {
  background: var(--bg-secondary);
  padding: 24px;
  border-radius: 16px;
  border-left: 4px solid var(--wa-green);
}

.content-block h3 {
  font-size: 1.2rem;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.content-block ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.content-block li {
  margin-bottom: 12px;
}

.content-block a {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.content-block a:hover {
  color: var(--wa-green);
}

.content-block p {
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 16px;
  font-size: 0.95rem;
}

.cta-link {
  display: inline-block;
  color: var(--wa-green);
  font-weight: 600;
  text-decoration: none;
  margin-top: 8px;
}

.cta-link:hover {
  text-decoration: underline;
}

/* Comprehensive Guide Section */
.comprehensive-guide {
  background: var(--bg-primary);
  padding: 60px 20px 100px;
  border-top: 1px solid var(--soft-border);
}

.guide-container {
  max-width: 800px;
  margin: 0 auto;
}

.guide-container h2 {
  font-size: 1.8rem;
  color: var(--text-primary);
  margin: 40px 0 20px;
  line-height: 1.3;
}

.guide-container h2:first-child {
  margin-top: 0;
  font-size: 2.2rem;
  color: var(--text-primary);
}

.guide-container p {
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 20px;
}

.guide-container strong {
  color: var(--text-primary);
}

.guide-container code {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  color: var(--text-primary);
}

/* Responsive */
@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2rem;
  }
  
  .accordion-header {
    font-size: 1rem;
  }
}
</style>
